/**
 * « Chapitre non fiable » devient la correction du chapitre (« Générer... » d'une activité) : une règle, des
 * mots et un corps d'appel, lus à l'identique par les trois surfaces — l'ancienne fenêtre
 * (`ParcoursChoiceModal.js`), « Générer à partir de l'exo » et le fil de la salle du nouvel espace.
 *
 * Un support dont le chapitre n'est pas sûr (`sourceChapterUnreliable`) ne génère pas de semblables. Le masteur
 * choisit son chapitre sur place, dans la liste de la matière du support et de la classe de l'élève ; le choix
 * part par la correction du chapitre (`PATCH materials/{id}/chapter`), qui rend le support fiable. Quand la
 * matière n'a aucun chapitre dans cette classe, il n'y a rien à choisir : le masteur confirme le support sans
 * chapitre (le même appel, `chapter_id: null`), et la génération part.
 */

type Id = string | number

/**
 * Un chapitre détecté sous la confiance `high` ne fait pas d'exercice semblable fiable : la génération depuis
 * cet exo attend que le masteur choisisse son chapitre. Une confiance inconnue laisse passer, comme la prod.
 */
export function sourceChapterUnreliable(confidence: string | null | undefined): boolean {
    return confidence != null && confidence !== "high"
}

/**
 * Où en est la correction :
 *  - `reliable` : le support est sûr, rien à corriger ;
 *  - `loading` : la liste des chapitres arrive ;
 *  - `pick` : des chapitres à choisir ;
 *  - `empty` : aucun chapitre pour la matière dans cette classe, la génération part sans chapitre ;
 *  - `failed` : la liste n'a pas pu se charger, on la relit.
 */
export type SourceChapterFixStatus = "reliable" | "loading" | "pick" | "empty" | "failed"

/** `count` : le nombre de chapitres de la liste, nul tant qu'elle n'est pas arrivée. */
export function sourceChapterFixStatus(
    unreliable: boolean,
    count: number | null,
    failed: boolean
): SourceChapterFixStatus {
    if (!unreliable) return "reliable"
    if (failed) return "failed"
    if (count == null) return "loading"
    return count > 0 ? "pick" : "empty"
}

/**
 * La confiance du support, corrigée de ce que le masteur vient d'enregistrer : la réponse de la correction
 * (`state.data.materials.chapterConfidenceById`) l'emporte sur l'activité que l'écran tient, que personne ne
 * relit avant la prochaine lecture de l'élève.
 */
export function effectiveSourceConfidence(
    materialId: Id | null | undefined,
    confidence: string | null | undefined,
    confirmedById: Record<string, string | null | undefined> | null | undefined
): string | null {
    if (materialId != null && confirmedById != null && String(materialId) in confirmedById) {
        return confirmedById[String(materialId)] ?? null
    }
    return confidence ?? null
}

/** Le support est-il à corriger, une fois la correction du masteur lue ? */
export function sourceNeedsChapter(
    materialId: Id | null | undefined,
    confidence: string | null | undefined,
    confirmedById: Record<string, string | null | undefined> | null | undefined
): boolean {
    return sourceChapterUnreliable(effectiveSourceConfidence(materialId, confidence, confirmedById))
}

/** Le corps de la correction : le chapitre choisi, ou aucun, avec la classe où la matière n'en a pas. */
export function sourceChapterFixBody(
    chapterId: Id | null,
    className: string | null
): { chapter_id: number } | { chapter_id: null; class?: string } {
    if (chapterId != null) return { chapter_id: Number(chapterId) }
    return className != null ? { chapter_id: null, class: className } : { chapter_id: null }
}

/**
 * Ce que l'avertissement dit au masteur, selon où en est la correction ; rien une fois le support sûr. `scope` :
 * « 3ème · Arabe » (`generationScopeLabel`), nul quand la classe ou la matière manque.
 */
export function sourceChapterFixNotice(status: SourceChapterFixStatus, scope: string | null): string | null {
    if (status === "reliable") return null
    if (status === "empty") return SOURCE_CHAPTER_FIX_TEXTS.empty(scope)
    if (status === "failed") return SOURCE_CHAPTER_FIX_TEXTS.failed
    return SOURCE_CHAPTER_FIX_TEXTS.pick
}

export const SOURCE_CHAPTER_FIX_TEXTS = {
    title: "Chapitre non fiable",
    pick: "Le chapitre de cet exo n'est pas sûr : choisis-le, puis génère.",
    empty: (scope: string | null) =>
        `Aucun chapitre ${scope != null ? `en ${scope} ` : ""}dans notre programme : les exercices seront générés sans chapitre.`,
    failed: "La liste des chapitres n'a pas pu se charger.",
    retry: "Réessayer",
    saving: "Enregistrement du chapitre…",
    saveFailed: "Le chapitre n'a pas pu être enregistré. Réessaie.",
    saved: "Chapitre enregistré : tu peux générer.",
    withoutChapter: "Générer sans chapitre",
    /** Un élève ou un parent : la correction est réservée au masteur (`manage_live_sessions`). */
    waiting:
        "Détecté avec une confiance basse — ton tuteur doit corriger le chapitre avant qu'un exercice similaire puisse être généré.",
    waitingLabel: "En attente de correction",
}
